/**
 * PrintIssueTickets — list page for AISC §1.7.0 Phase 4.
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from "@mui/material";
import { HomeRounded, AddRounded, WarningAmberRounded, CheckCircleOutlineRounded, LocalShippingRounded, CancelRounded, PrintOutlined } from "@mui/icons-material";

import {
  listPits, printPit, deliverPit, cancelPit,
  PIT_STATUSES, PIT_DEPARTMENTS,
} from "../../utils/printIssueTicketsApi";
import EmptyState from "../../components/EmptyState/EmptyState";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLOR = {
  REQUESTED: { bg: "#fff3cd", color: "#664d03" },
  PRINTED:   { bg: "#cfe2ff", color: "#052c65" },
  DELIVERED: { bg: "#d1e7dd", color: "#0a3622" },
  CANCELLED: { bg: "#f8d7da", color: "#58151c" },
};

export default function PrintIssueTickets() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobIdParam = params.get("job_id") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [deptFilter, setDeptFilter] = useState("ALL");
  const [acting, setActing] = useState(false);

  // Inline cancel dialog
  const [openCancel, setOpenCancel] = useState(null); // pit row or null
  const [cancelReason, setCancelReason] = useState("");

  const refresh = () => {
    setLoading(true); setError(null); setNotAvailable(false);
    const filters = {};
    if (jobIdParam) filters.job_id = jobIdParam;
    if (statusFilter && statusFilter !== "ALL") filters.status = statusFilter;
    if (deptFilter && deptFilter !== "ALL") filters.issued_to_department = deptFilter;
    listPits(filters)
      .then((data) => setRows((data && data.rows) || []))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 501) setNotAvailable(true);
        else setError(err?.response?.data?.error || err?.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [jobIdParam, statusFilter, deptFilter]);

  const handlePrint = async (pit) => { setActing(true); try { await printPit(pit.id); refresh(); } catch (e) { setError(e?.response?.data?.error || e?.message); } finally { setActing(false); } };
  const handleDeliver = async (pit) => { setActing(true); try { await deliverPit(pit.id); refresh(); } catch (e) { setError(e?.response?.data?.error || e?.message); } finally { setActing(false); } };
  const handleCancel = async () => {
    if (!cancelReason || cancelReason.trim().length < 4) { setError("Reason min 4 chars"); return; }
    setActing(true); try { await cancelPit(openCancel.id, cancelReason); setOpenCancel(null); setCancelReason(""); refresh(); }
    catch (e) { setError(e?.response?.data?.error || e?.message); } finally { setActing(false); }
  };

  return (
    <>
      <Helmet><title>Print Issue Tickets | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          <Typography color="text.primary">Print Issue Tickets</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Print Issue Tickets</Typography>
          <Button variant="contained" startIcon={<AddRounded />}
            onClick={() => navigate(jobIdParam ? `/print-issue-tickets/new?job_id=${jobIdParam}` : "/print-issue-tickets/new")}>
            New Ticket
          </Button>
        </Stack>
        <PageGuide
          why="Control which drawing revision actually reaches the shop floor — so no one builds from an out-of-date print."
          who="Whoever issues prints to the floor; supervisors controlling the current revision."
          what="A record of which drawing print was issued, to whom, and at what revision."
          how="Create a ticket naming the drawing, revision, and the department or crew it went to."
          when="Each time a controlled print is released to the floor."
          standard="AISC §1.7.0"
        />

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField select label="Status" size="small" sx={{ minWidth: 160 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="ALL">All statuses</MenuItem>
              {PIT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select label="Department" size="small" sx={{ minWidth: 180 }} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
              <MenuItem value="ALL">All departments</MenuItem>
              {PIT_DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Stack>
        </Paper>

        {notAvailable && <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>Phase 4 backend deploy hasn't landed.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <SmartTable tableId="print-issue-tickets" lockedColumns={["Ticket", "Drawing", "Rev", "Status"]}>
            <TableHead><TableRow>
              <TableCell>Ticket</TableCell><TableCell>Drawing</TableCell><TableCell>Rev</TableCell>
              <TableCell>Issue to</TableCell><TableCell>Purpose</TableCell>
              <TableCell>Copies</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#6c757d" }}>Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && !notAvailable && !error && (
                <TableRow><TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                  <EmptyState
                    icon={PrintOutlined}
                    title="No print issue tickets yet"
                    description="Click New Ticket to authorize a printed-copy issue. Tracks the lifecycle from REQUESTED through PRINTED and DELIVERED so the controlled-copy chain stays auditable."
                    variant="plain"
                  />
                </TableCell></TableRow>
              )}
              {!loading && rows.map((pit) => {
                const colors = STATUS_COLOR[pit.status] || { bg: "#e9ecef", color: "#495057" };
                return (
                  <TableRow key={pit.id} hover>
                    <TableCell><strong>{pit.ticket_number}</strong></TableCell>
                    <TableCell>Doc #{pit.document_id}</TableCell>
                    <TableCell>{pit.drawing_revision}</TableCell>
                    <TableCell>{pit.issued_to_department}{pit.issued_to_other_text ? ` (${pit.issued_to_other_text})` : ""}</TableCell>
                    <TableCell>{pit.purpose}</TableCell>
                    <TableCell>{pit.copies}</TableCell>
                    <TableCell><Chip size="small" label={prettyStatus(pit.status)}
                      sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600, fontSize: "0.7rem" }} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {pit.status === "REQUESTED" && (
                          <Button size="small" startIcon={<CheckCircleOutlineRounded />} onClick={() => handlePrint(pit)} disabled={acting}>Print</Button>
                        )}
                        {pit.status === "PRINTED" && (
                          <Button size="small" startIcon={<LocalShippingRounded />} onClick={() => handleDeliver(pit)} disabled={acting}>Deliver</Button>
                        )}
                        {(pit.status === "REQUESTED" || pit.status === "PRINTED") && (
                          <Button size="small" startIcon={<CancelRounded />} color="error" onClick={() => setOpenCancel(pit)} disabled={acting}>Cancel</Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>

      <Dialog open={!!openCancel} onClose={() => setOpenCancel(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel {openCancel?.ticket_number}</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="Reason (min 4 chars)" required
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancel(null)} disabled={acting}>Keep open</Button>
          <Button variant="contained" color="error" onClick={handleCancel} disabled={acting}>{acting ? "Cancelling…" : "Cancel ticket"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
